import { createStore } from 'redux';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage/session';

const initialState = {
    userGames: [],
    // False until DashboardHeader's catalogue fetch for the current org settles, so a page listing
    // `userGames` can tell "still loading" from "this org has no games".
    userGamesLoaded: false,
    // Games in the selected org the user is SUSPENDED on (an owner's decision, not "never granted"), published
    // by DashboardHeader with userGames. The Games page shows them, muted, so a suspended game does not just
    // vanish; nothing selects or opens them.
    suspendedGames: [],
    // The rest of the org's games: ones the user was never given, in a state worth asking for (see
    // requestableGames in switcherGames.js), published by DashboardHeader with userGames. The Games page lists
    // them under "Other games in <org>" with a Request access button; the nav and the switcher never do.
    requestableGames: [],
    // The signed-in user's own portfolios in the selected org, published by DashboardHeader (same
    // ownership as userGames). `Loaded` tells "still loading" from "none yet".
    userPortfolios: [],
    userPortfoliosLoaded: false,
    completePortfolioGames: [],
    userDetails: {
        userId: null,
        userName: null,
        orgs: [],       // array of { orgId, orgState }
        roleId: null,
        superUser: false,
        // True once fresh user data has been loaded from the server this session. NOT persisted
        // (reset on rehydrate) so route guards don't act on stale/missing flags after a refresh —
        // notably, they must not bounce a (no-org) super-user to onboarding before superUser loads.
        userDetailsLoaded: false
    },
    dashboardContext: {
        selectedOrgId: null,
        selectedOrgName: null,
        selectedGameId: null,
        selectedGameName: null,
        selectedGameClientId: null,
        selectedSegmentId: null,    // active geo segment (owning-geography id); null = Global (all players)
        selectedRegionIds: [],      // multiselect regions
        navScope: 'hub',            // focused sidebar scope; the URL wins when it owns one (see routes/navScopes.jsx)
        userRole: null,             // 'SUPER_USER' | 'REGULAR_USER' (super-user gate only; owner-tier UX uses the flags below)
        isOrgOwner: false,          // caller is org owner-tier (owner/co-owner) for the selected org — server invitationLevel
        ownsAnyGame: false,         // caller owns ≥1 game in the org (server `owned` flag) — drives owner-only management UI
        defaultStartPage: 'home',   // global; mirrors userPreferences.globalPreferences.defaultLandingPage
        sidebarOrder: {},           // global; { [orderKey]: [itemId, ...] } — per-section drag order
        sidebarPinned: [],          // global; paths pinned in the sidebar
        homeWidgetsHidden: [],      // global; list of widget IDs hidden on dashboard-home
        homeWidgetsOrder: [],       // global; user-defined render order for dashboard-home widgets (empty = use default)
        kpiSavedViews: [],          // global; named filter snapshots for the home KPI grid — [{ name, segment, dateRange, metric, startDate, endDate, visible }]
        recentItems: {}             // global; { [orgId_gameId_clientId]: [...entries] } — home dashboard recents
    },
    effectivePermissions: null,     // { roleId, roleName, defaultPermissionLevel, pagePermissions, componentPermissions }

    // Snapshot of "do I have access to any game in the currently selected org?".
    // Populated by RequireGameAccess on org switch via
    // getGameRegistriesByOrgIdWithAccessibility — accessibleCount === 0 with
    // loaded === true means the user is in the org but hasn't been granted
    // access to any specific game yet, which we use to show a placeholder on
    // live-ops / dashboard pages. Kept out of persist (security: a stale "have
    // access" value could let a removed user briefly see gated UI before the
    // refresh resolves).
    gameAccessibility: {
        orgId: null,                // org this snapshot belongs to
        gameId: null,               // selected game when this snapshot was taken
        loaded: false,              // false = not yet fetched (or org/game just changed)
        accessibleCount: 0,         // games in this org that the user can open
        total: 0                    // games in this org overall (for diagnostics)
    },
    // Bumped by useAccessRefresh each time it re-reads the user's access after a change (the server's signal, a tab
    // coming back, a refusal that turned out to be a change). Anything that holds its own copy of access data - the
    // game-access guard's snapshot, Profile's Organizations & Games, the Games page's request states - re-reads when
    // it moves, including a page mounted after the change: a window event reaches only what is mounted at that
    // moment, so a change that landed while the user sat on Profile left the guard's snapshot saying "suspended".
    accessVersion: 0
};

// Strip security-sensitive fields from persisted state — must always be derived fresh from the server.
//
// - effectivePermissions: fetched fresh on every load (already stripped before this change)
// - userDetails.superUser: a stale true value causes DashboardHeader to compute SUPER_USER role
//   and fetch FULL_ACCESS permissions before DashboardLayout's init can correct it, resulting
//   in all nav items showing for users who should have restricted access.
// - dashboardContext.userRole / isOrgOwner / ownsAnyGame: derived from isSuperUser + org/access data;
//   a stale owner value briefly shows owner-only UI to a demoted user, so all three are reset and
//   re-computed from fresh data on load.
//
// Note: blacklist only works with combineReducers; for a flat reducer we need a transform.
const stripEffectivePermissions = createTransform(
    (state) => {
        if (!state) return state;
        const { effectivePermissions, userDetails, dashboardContext, gameAccessibility, ...rest } = state;
        const { superUser, ...userDetailsRest } = userDetails || {};
        const { userRole, isOrgOwner, ownsAnyGame, ...dashCtx } = dashboardContext || {};
        return { ...rest, userDetails: userDetailsRest, dashboardContext: dashCtx };
    },
    (state) => {
        if (!state) return state;
        return {
            ...state,
            effectivePermissions: null,
            userDetails: { ...(state.userDetails || {}), superUser: false, userDetailsLoaded: false },
            dashboardContext: { ...(state.dashboardContext || {}), userRole: null, isOrgOwner: false, ownsAnyGame: false },
            // Always derive game accessibility fresh on load — a stale snapshot
            // could let a user whose access was just revoked briefly see gated
            // UI before RequireGameAccess re-fetches.
            gameAccessibility: { orgId: null, gameId: null, loaded: false, accessibleCount: 0, total: 0 }
        };
    }
);

// Configure persist options
const persistConfig = {
    key: 'root',
    storage,
    transforms: [stripEffectivePermissions]
};

function rootReducer(state = initialState, action) {
    switch (action.type) {
        case 'SET_USER_GAMES':
            return { ...state, userGames: action.payload };
        case 'SET_USER_GAMES_LOADED':
            return { ...state, userGamesLoaded: action.payload };
        case 'SET_SUSPENDED_GAMES':
            return { ...state, suspendedGames: action.payload };
        case 'SET_REQUESTABLE_GAMES':
            return { ...state, requestableGames: action.payload };
        case 'SET_USER_PORTFOLIOS':
            return { ...state, userPortfolios: action.payload };
        case 'SET_USER_PORTFOLIOS_LOADED':
            return { ...state, userPortfoliosLoaded: action.payload };
        case 'SET_COMPLETE_PORTFOLIO_GAMES':
            return { ...state, completePortfolioGames: action.payload };
        case 'SET_USER_DATA_FROM_SERVER':
            return { ...state, userDataFromServer: action.payload };
        case 'SET_USER_INFO':
            // Mark loaded — this payload comes from a fresh server getUser, so route guards can now
            // trust superUser/orgs. (UPDATE_USER_INFO preserves the flag via the spread below.)
            return { ...state, userDetails: { ...action.payload, userDetailsLoaded: true } };
        case 'UPDATE_USER_INFO':
            return {
                ...state,
                userDetails: { ...state.userDetails, ...action.payload }
            };
        case 'ADD_ORG':
            return {
                ...state,
                userDetails: {
                    ...state.userDetails,
                    orgs: [
                        ...(state.userDetails.orgs || []),
                        action.payload
                    ]
                }
            };
        case 'SET_DASHBOARD_CONTEXT':
            return { ...state, dashboardContext: action.payload };
        case 'UPDATE_DASHBOARD_CONTEXT':
            return {
                ...state,
                dashboardContext: { ...state.dashboardContext, ...action.payload }
            };
        case 'SET_EFFECTIVE_PERMISSIONS':
            return { ...state, effectivePermissions: action.payload };
        case 'SET_GAME_ACCESSIBILITY':
            // Full replacement — payload always carries the org this snapshot
            // applies to so consumers can detect "stale" data (orgId mismatch).
            return { ...state, gameAccessibility: action.payload };
        case 'BUMP_ACCESS_VERSION':
            return { ...state, accessVersion: (state.accessVersion || 0) + 1 };
        case 'RESET_GAME_ACCESSIBILITY':
            return {
                ...state,
                gameAccessibility: { orgId: null, gameId: null, loaded: false, accessibleCount: 0, total: 0 }
            };
        case 'RESET_STORE':
            // Logout reset, so a same-tab re-login can't inherit the previous user.
            return initialState;
        default:
            return state;
    }
}

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const setUserInformation = (userDetails) => ({
    type: 'SET_USER_INFO',
    payload: userDetails
});

// Merge a partial update into userDetails without replacing the whole object
// (preserves superUser / userDetailsLoaded). Used when a single field changes
// after login — e.g. the Profile page renaming the user via userName.
export const updateUserInformation = (partialUserDetails) => ({
    type: 'UPDATE_USER_INFO',
    payload: partialUserDetails
});

export const addOrg = (org) => ({
    type: 'ADD_ORG',
    payload: org
});

// The selected org's game catalogue, published by DashboardHeader (which owns the fetch and its
// staleness guards) so the sidebar's Games list can read it without a second request.
export const setUserGames = (games) => ({
    type: 'SET_USER_GAMES',
    payload: games
});

export const setSuspendedGames = (games) => ({
    type: 'SET_SUSPENDED_GAMES',
    payload: games
});

export const setRequestableGames = (games) => ({
    type: 'SET_REQUESTABLE_GAMES',
    payload: games
});

export const setUserGamesLoaded = (loaded) => ({
    type: 'SET_USER_GAMES_LOADED',
    payload: loaded
});

export const setUserPortfolios = (portfolios) => ({
    type: 'SET_USER_PORTFOLIOS',
    payload: portfolios
});

export const setUserPortfoliosLoaded = (loaded) => ({
    type: 'SET_USER_PORTFOLIOS_LOADED',
    payload: loaded
});

export const setDashboardContext = (context) => ({
    type: 'SET_DASHBOARD_CONTEXT',
    payload: context
});

export const updateDashboardContext = (partialContext) => ({
    type: 'UPDATE_DASHBOARD_CONTEXT',
    payload: partialContext
});

export const setEffectivePermissions = (permissions) => ({
    type: 'SET_EFFECTIVE_PERMISSIONS',
    payload: permissions
});

// Persist a fresh accessibility snapshot for an org. Always include the orgId
// so consumers can tell when the snapshot belongs to a previously-selected org
// (e.g. user just switched and a stale value is briefly still in state).
// `games`: the org catalogue with the server's `accessible` flag ({ id, orgId, gameName, accessible }), or undefined
// when it could not be read - what decides whether a /game/<slug> URL names a game the user can open.
// `accessVersion`: the store's accessVersion when the read started, so a snapshot taken before an access change is
// known to be stale (snapshotIsCurrent in routeGameAccess.js).
export const setGameAccessibility = ({ orgId, gameId = null, accessibleCount, total, games, accessVersion = 0 }) => ({
    type: 'SET_GAME_ACCESSIBILITY',
    payload: { orgId, gameId, loaded: true, accessibleCount, total, games, accessVersion }
});

/** The user's access was just re-read after a change: every private copy of access data is now stale. */
export const bumpAccessVersion = () => ({
    type: 'BUMP_ACCESS_VERSION'
});

export const resetGameAccessibility = () => ({
    type: 'RESET_GAME_ACCESSIBILITY'
});

export const resetStore = () => ({
    type: 'RESET_STORE'
});

export const ReduxStore = createStore(
    persistedReducer,
    window.__REDUX_DEVTOOLS_EXTENSION__ && window.__REDUX_DEVTOOLS_EXTENSION__()
);

// Create persistor
export const persistor = persistStore(ReduxStore);
